import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, LogIn } from 'lucide-react';

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Services', href: '/services' },
    { name: 'Our Team', href: '/team' },
    { name: 'Contact', href: '/contact' }
  ];

  // Header styling based on page and scroll position
  const getHeaderClasses = () => {
    if (isHomePage) {
      // Home page: transparent when at top, dark when scrolled
      return scrolled 
        ? 'bg-black/20 backdrop-blur-lg border-b border-white/20' 
        : 'bg-transparent';
    } else {
      // Other pages: always visible with white background
      return 'bg-white/95 backdrop-blur-lg shadow-lg border-b border-gray-100';
    }
  };

  return (
    <header className={`fixed top-0 w-full z-50 transition-all duration-500 ${getHeaderClasses()}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex justify-between items-center h-18">
          
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <div className={`relative rounded-xl p-3 ${
              isHomePage ? 'bg-white/15 backdrop-blur-sm border border-white/20' : 'bg-white shadow-sm border border-gray-200'
            }`}>
              <img 
                src="https://customer-assets.emergentagent.com/job_mande-experts/artifacts/z0cnnuyb_Logo-%20Evidentia.jpeg" 
                alt="Evidentia Logo" 
                className="h-12 w-auto transition-all duration-300 group-hover:scale-105"
                onError={(e) => {
                  const parent = e.target.parentElement;
                  parent.innerHTML = `<div class="text-2xl font-bold ${isHomePage ? 'text-white drop-shadow-lg' : 'text-gray-900'}">EVIDENTIA AFRICA</div>`;
                }}
              />
              <div className={`absolute -inset-1 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm ${
                isHomePage ? 'bg-white/10' : 'bg-teal-100'
              }`}></div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-2">
            {navItems.map((item) => (
              <Link 
                key={item.name}
                to={item.href} 
                className={`relative px-6 py-3 font-semibold transition-all duration-300 group rounded-full ${
                  location.pathname === item.href 
                    ? (isHomePage ? 'text-white bg-white/20 backdrop-blur-sm' : 'text-teal-600 bg-teal-50')
                    : (isHomePage ? 'text-white/90 hover:text-white' : 'text-gray-700 hover:text-teal-600')
                }`}
              >
                <span className="relative z-10">{item.name}</span>
                <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 rounded-full transition-all duration-300 ${
                  isHomePage ? 'bg-white/10' : 'bg-teal-50'
                }`}></div>
                <div className={`absolute bottom-0 left-1/2 h-0.5 transition-all duration-300 ${
                  isHomePage ? 'bg-white' : 'bg-teal-500'
                } ${
                  location.pathname === item.href ? 'w-6 left-[calc(50%-0.75rem)]' : 'w-0 group-hover:w-6 group-hover:left-[calc(50%-0.75rem)]'
                }`}></div>
              </Link>
            ))}
          </nav>

          {/* Login Button */}
          <div className="hidden lg:flex items-center space-x-4">
            <a 
              href="https://mail.hostinger.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className={`shadow-lg group ${
                isHomePage 
                  ? 'bg-white/20 hover:bg-white/30 text-white border border-white/30 hover:border-white/50 backdrop-blur-sm'
                  : 'bg-teal-500 hover:bg-teal-600 text-white border border-teal-500 hover:border-teal-600'
              }`}>
                <LogIn className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform duration-300" />
                Login
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`lg:hidden relative p-3 rounded-full transition-all duration-300 ${
              isHomePage 
                ? 'text-white/90 hover:text-white hover:bg-white/10'
                : 'text-gray-700 hover:text-teal-600 hover:bg-teal-50'
            }`}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className={`lg:hidden absolute top-full left-0 w-full backdrop-blur-lg ${
            isHomePage 
              ? 'bg-black/80 border-t border-white/20'
              : 'bg-white/95 border-t border-gray-100 shadow-xl'
          }`}>
            <div className="px-6 py-8 space-y-2">
              {navItems.map((item) => (
                <Link 
                  key={item.name}
                  to={item.href} 
                  className={`block px-4 py-3 font-semibold rounded-xl transition-all duration-300 ${
                    location.pathname === item.href 
                      ? (isHomePage ? 'text-white bg-white/20' : 'text-teal-600 bg-teal-50')
                      : (isHomePage ? 'text-white/90 hover:text-white hover:bg-white/10' : 'text-gray-700 hover:text-teal-600 hover:bg-teal-50')
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              <div className="pt-4">
                <a 
                  href="https://mail.hostinger.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className={`w-full ${
                    isHomePage 
                      ? 'bg-white/20 hover:bg-white/30 text-white border border-white/30 hover:border-white/50 backdrop-blur-sm'
                      : 'bg-teal-500 hover:bg-teal-600 text-white'
                  }`}>
                    <LogIn className="w-4 h-4 mr-2" />
                    Login
                  </Button>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};